// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { OperationOutcome } from '@medplum/fhirtypes';

export const SCHEDULING_ERROR_CATEGORIES = {
  configurationInvalid: 'SCHEDULING_CONFIGURATION_INVALID',
  timeUnavailable: 'SCHEDULING_TIME_UNAVAILABLE',
  timezoneMissing: 'SCHEDULING_TIMEZONE_MISSING',
  serviceNotFound: 'SCHEDULING_SERVICE_NOT_FOUND',
  permissionDenied: 'SCHEDULING_PERMISSION_DENIED',
  appointmentAlreadyBooked: 'SCHEDULING_APPOINTMENT_ALREADY_BOOKED',
  operationUnsupported: 'SCHEDULING_OPERATION_UNSUPPORTED',
  finalizationIncomplete: 'SCHEDULING_FINALIZATION_INCOMPLETE',
  holdExpired: 'SCHEDULING_HOLD_EXPIRED',
} as const;

export type SchedulingErrorCategory = (typeof SCHEDULING_ERROR_CATEGORIES)[keyof typeof SCHEDULING_ERROR_CATEGORIES];

export interface SchedulingErrorContext {
  operation?: string;
  resourceType?: 'HealthcareService' | 'Schedule' | 'Appointment';
}

export class SchedulingError extends Error {
  readonly category: SchedulingErrorCategory;
  readonly outcome?: OperationOutcome;
  readonly diagnostics?: string;
  readonly cause?: unknown;

  constructor(
    category: SchedulingErrorCategory,
    message: string,
    options?: { outcome?: OperationOutcome; diagnostics?: string; cause?: unknown }
  ) {
    super(message);
    this.name = 'SchedulingError';
    this.category = category;
    this.outcome = options?.outcome;
    this.diagnostics = options?.diagnostics;
    this.cause = options?.cause;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isOperationOutcome(value: unknown): value is OperationOutcome {
  return isRecord(value) && value.resourceType === 'OperationOutcome' && Array.isArray(value.issue);
}

function findOutcome(error: unknown): OperationOutcome | undefined {
  if (isOperationOutcome(error)) {
    return error;
  }
  if (isRecord(error) && isOperationOutcome(error.outcome)) {
    return error.outcome;
  }
  return undefined;
}

function getDiagnostics(error: unknown, outcome: OperationOutcome | undefined): string {
  const outcomeText = outcome?.issue
    .map((issue) => [issue.code, issue.details?.text, issue.diagnostics].filter(Boolean).join(': '))
    .filter((value): value is string => Boolean(value))
    .join('; ');
  if (outcomeText) {
    return outcomeText;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return typeof error === 'string' ? error : 'Scheduling operation failed';
}

function resourceTypeFromDiagnostics(diagnostics: string): SchedulingErrorContext['resourceType'] {
  const match = diagnostics.match(/\b(HealthcareService|Schedule|Appointment)\//);
  if (match?.[1] === 'HealthcareService' || match?.[1] === 'Schedule' || match?.[1] === 'Appointment') {
    return match[1];
  }
  return undefined;
}

function categoryForOutcome(
  outcome: OperationOutcome | undefined,
  diagnostics: string,
  context?: SchedulingErrorContext
): SchedulingErrorCategory {
  for (const issue of outcome?.issue ?? []) {
    if (issue.code === 'not-found') {
      const resourceType = resourceTypeFromDiagnostics(diagnostics) ?? context?.resourceType;
      return resourceType === 'HealthcareService'
        ? SCHEDULING_ERROR_CATEGORIES.serviceNotFound
        : SCHEDULING_ERROR_CATEGORIES.configurationInvalid;
    }
    if (issue.code === 'forbidden') {
      return SCHEDULING_ERROR_CATEGORIES.permissionDenied;
    }
    if (issue.code === 'conflict') {
      return SCHEDULING_ERROR_CATEGORIES.appointmentAlreadyBooked;
    }
    if (issue.code === 'invalid' && /unavailable|not available|no availability|busy/.test(diagnostics.toLowerCase())) {
      return SCHEDULING_ERROR_CATEGORIES.timeUnavailable;
    }
  }

  const normalized = diagnostics.toLowerCase();
  if (/permission|forbidden|unauthori[sz]ed|access denied/.test(normalized)) {
    return SCHEDULING_ERROR_CATEGORIES.permissionDenied;
  }
  if (/timezone|time zone/.test(normalized)) {
    return SCHEDULING_ERROR_CATEGORIES.timezoneMissing;
  }
  if (/healthcareservice|service type/.test(normalized) && /not found|missing|unknown/.test(normalized)) {
    return SCHEDULING_ERROR_CATEGORIES.serviceNotFound;
  }
  if (/already booked|already reserved|already.*appointment|conflict/.test(normalized)) {
    return SCHEDULING_ERROR_CATEGORIES.appointmentAlreadyBooked;
  }
  if (/unavailable|not available|no availability|not schedulable|outside.*window|busy/.test(normalized)) {
    return SCHEDULING_ERROR_CATEGORIES.timeUnavailable;
  }
  if (/unsupported|not supported|unknown operation/.test(normalized)) {
    return SCHEDULING_ERROR_CATEGORIES.operationUnsupported;
  }
  return SCHEDULING_ERROR_CATEGORIES.configurationInvalid;
}

export function parseSchedulingError(error: unknown, context?: SchedulingErrorContext): SchedulingError {
  if (error instanceof SchedulingError) {
    return error;
  }
  const outcome = findOutcome(error);
  const diagnostics = getDiagnostics(error, outcome);
  return new SchedulingError(
    categoryForOutcome(outcome, diagnostics, context),
    'The scheduling operation could not be completed.',
    {
      outcome,
      diagnostics,
      cause: error,
    }
  );
}
